import { RemixBrowser } from "@remix-run/react";
import { startTransition, StrictMode } from "react";
import { hydrateRoot } from "react-dom/client";
import { initSentryClient } from "~/utils/sentry.client";

// Initialize Sentry client-side if DSN is available
const sentryDsn = (window as any).ENV?.SENTRY_DSN;
if (sentryDsn) {
  initSentryClient(sentryDsn);
}

/**
 * Capture hydration errors with component stack so we can identify which
 * component is causing React #418/#423 in production. Microsoft Clarity
 * recordings (2026-06-05) showed ~26 sessions in 3 days hitting hydration
 * errors on product + collection pages — customers visibly gave up right
 * after the errors fired. Without the component stack, we can't tell WHICH
 * component is mismatching.
 *
 * `onRecoverableError` fires when React falls back from hydration to CSR —
 * this is the #418/#423 path. The second argument's `componentStack`
 * pinpoints the failing component, which is everything for fixing the bug.
 *
 * Sends to window.Sentry if loaded (initSentryClient sets it up), else
 * console-warns so we can see in DevTools while debugging locally.
 */
function reportRecoverableError(
  error: unknown,
  errorInfo: { componentStack?: string },
) {
  const sentry = (window as any).Sentry;
  const componentStack = errorInfo?.componentStack || "(no stack)";
  const message = error instanceof Error ? error.message : String(error);
  if (sentry?.captureException) {
    sentry.captureException(error, {
      tags: { hydration: "recoverable" },
      contexts: {
        react: { componentStack },
        url: { href: window.location.href },
      },
    });
  } else {
    // eslint-disable-next-line no-console
    console.warn("[hydration:recoverable]", message, componentStack);
  }
}

startTransition(() => {
  hydrateRoot(
    document,
    <StrictMode>
      <RemixBrowser />
    </StrictMode>,
    {
      onRecoverableError: (error, errorInfo) =>
        reportRecoverableError(error, errorInfo),
    },
  );
});
